/**
 * WorkspaceContext - unified conversation model (Gemini-style).
 *
 * One workspace = one ordered thread of turns. Every turn has its own `type`
 * (text / image / video / music); switching the prompt-bar mode only changes
 * what the NEXT send will be - it never wipes the thread.
 */
import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MODE_IDS, MODELS as FALLBACK_MODELS } from './tokens';
import { fetchModels, sendChat, sendChatStream, buildMessagesFromTurns, detectIntent, generateUnified } from './workspaceAI';
import keiAIService from '../services/keiAIService';
import { useAuth } from '../contexts/AuthContext';
import * as contentService from '../services/contentService';

const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.daggpt.network');

// ─── localStorage persistence (Atelier) ───────────────────────
const STORAGE_KEY = 'daggpt:atelier:v1';

function loadStore(userId = 'anonymous') {
  try {
    const key = `daggpt:atelier:v1:${userId}`;
    const raw = localStorage.getItem(key);
    
    // BACKWARD COMPATIBILITY: If there's no user-specific key yet but legacy data exists,
    // and the user is authenticated (not anonymous), copy existing active workspace projects over!
    if (!raw && userId !== 'anonymous') {
      const legacy = localStorage.getItem(STORAGE_KEY);
      if (legacy) {
        localStorage.setItem(key, legacy);
        // CRITICAL: Immediately remove the legacy key after first migration so subsequent 
        // logins/accounts on this machine do not copy the first account's folders!
        localStorage.removeItem(STORAGE_KEY);
        return JSON.parse(legacy);
      }
    }

    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch { return null; }
}

function saveStore(store, userId = 'anonymous') {
  try {
    const key = `daggpt:atelier:v1:${userId}`;
    localStorage.setItem(key, JSON.stringify(store));
  } catch {}
}

// ─── Types (JS runtime - documented) ────────────────────────────────
// Turn = {
//   id: string
//   role: 'user' | 'assistant'
//   type: 'text' | 'image' | 'video' | 'music'
//   status: 'pending' | 'done' | 'error'
//   createdAt: number
//   text?: string
//   imageUrl?: string
//   videoUrl?: string
//   audioUrl?: string
//   meta?: { model?, aspect?, duration?, style?, vocal?, replyTo? }
//   error?: string
// }

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

// Per-mode draft controls (excluding the shared prompt). These persist across
// mode switches - the user's aspect/style/model choices for each mode stick.
// Models start from the fallback list and are replaced once /api/models resolves.
const makeDefaultControls = (models = FALLBACK_MODELS) => ({
  chat:  { model: models.chat[0]?.id,  webSearch: true },
  image: { model: models.image[0]?.id, aspect: '1:1',  style: 'Photorealistic' },
  video: { model: models.video[0]?.id, aspect: '16:9', style: 'Cinematic', duration: 5 },
  music: { model: models.music[0]?.id, style: 'Pop', vocal: true },
  slides: { model: models.chat[0]?.id, style: 'Professional', webSearch: false },
});

const makeEmptyConversation = () => ({
  id: uid(),
  title: 'New conversation',
  turns: [],
  createdAt: Date.now(),
  updatedAt: Date.now(),
});

const WorkspaceCtx = createContext(null);

export function WorkspaceProvider({ initialMode = 'chat', children }) {
  const { user, userProfile, refreshBalance } = useAuth() || {};
  const userId = user?.id || 'anonymous';

  // ─── Draft (prompt bar) ──────────────────────────────────────────
  const [activeMode, setActiveMode] = useState(
    MODE_IDS.includes(initialMode) ? initialMode : 'chat'
  );
  const [draftPrompt, setDraftPrompt] = useState('');
  const location = useLocation();

  // Intercept incoming landing page prompt redirection state (transparent pre-filling)
  useEffect(() => {
    if (location.state) {
      const { initialPrompt, initialMessage, selectedModel, aspectRatio, webSearch } = location.state;
      const promptToUse = initialPrompt || initialMessage;
      
      if (promptToUse) {
        setDraftPrompt(promptToUse);
      }
      
      if (selectedModel || aspectRatio || webSearch !== undefined) {
        setModes(prev => {
          const modeToConfigure = activeMode;
          const current = prev[modeToConfigure] || {};
          const updated = { ...current };
          
          if (selectedModel) updated.model = selectedModel;
          if (aspectRatio) updated.aspect = aspectRatio;
          if (webSearch !== undefined) updated.webSearch = webSearch;
          
          return {
            ...prev,
            [modeToConfigure]: updated
          };
        });
      }
      
      // Clear location state after parsing so subsequent edits are preserved
      window.history.replaceState({}, document.title);
    }
  }, [location.state, activeMode]);

  // Real catalog (hydrated from /api/models). Falls back to the curated
  // stubs in tokens.js until the network call returns.
  const [availableModels, setAvailableModels] = useState(FALLBACK_MODELS);
  const [defaultModels, setDefaultModels] = useState({});
  const [modes, setModes] = useState(() => makeDefaultControls(FALLBACK_MODELS));

  // Fetch real models once on mount.
  useEffect(() => {
    let cancelled = false;
    fetchModels()
      .then(real => {
        if (cancelled) return;
        // Only replace a mode's list if the API returned at least one model.
        const merged = {
          chat:  real.chat?.length  ? real.chat  : FALLBACK_MODELS.chat,
          image: real.image?.length ? real.image : FALLBACK_MODELS.image,
          video: real.video?.length ? real.video : FALLBACK_MODELS.video,
          music: real.music?.length ? real.music : FALLBACK_MODELS.music,
        };
        setAvailableModels(merged);
        if (real.defaults) setDefaultModels(real.defaults);
        
        // Re-point each mode's selected model to a real id, preferring a
        // sensible default (gpt-4o family) if present.
        setModes(prev => ({
          chat:  { ...prev.chat,  model: real.defaults?.['chat']?.id || pickDefaultChatModel(merged.chat)  || prev.chat.model  },
          image: { ...prev.image, model: real.defaults?.['image']?.id || merged.image[0]?.id || prev.image.model },
          video: { ...prev.video, model: real.defaults?.['video']?.id || merged.video[0]?.id || prev.video.model },
          music: { ...prev.music, model: real.defaults?.['music']?.id || merged.music[0]?.id || prev.music.model },
          slides: { ...prev.slides, model: real.defaults?.['chat']?.id || pickDefaultChatModel(merged.chat) || prev.slides.model, webSearch: false },
        }));
      })
      .catch(err => console.warn('[Workspace] fetchModels failed, using fallback:', err?.message));
    return () => { cancelled = true; };
  }, []);

  // ─── Conversation thread ─────────────────────────────────────────
  const [conversation, setConversation] = useState(makeEmptyConversation);
  const [conversations, setConversations] = useState([]); // archived threads
  const pendingTimers = useRef(new Map());
  const activeAbortController = useRef(null);

  // ─── Atelier projects ────────────────────────────────────────────
  // A project owns a list of conversation IDs. Default behaviour: each project
  // has ONE growing conversation; the user can call newConversationInProject
  // to start a second thread inside the same project.
  const [projects, setProjects] = useState([]);          // [{ id, title, color, starred, conversationIds, createdAt, updatedAt }]
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [expandedMedia, setExpandedMedia] = useState(null); // { url, type, title }
  const [activePreview, setActivePreview] = useState(null); // { type: 'spreadsheet', data, title }
  const [attachments, setAttachments] = useState([]);      // [{ id, url, type, name }]

  // ─── Intent detection (auto-detected on every draft change) ─────
  // Cached so the composer can render the chip without re-classifying on every render.
  const [intent, setIntent] = useState({ mode: 'chat', confidence: 0, source: 'default' });
  // When true, the user has manually overridden the auto-detected modality
  // for the current draft - don't auto-flip until they clear or send.
  const [intentLocked, setIntentLocked] = useState(false);

  // ─── Hydrate from localStorage & Cloud when active user changes ───
  const [loadedUserId, setLoadedUserId] = useState(null);
  const loadedUserIdRef = useRef(null);
  const lastSyncTimeRef = useRef(0);

  // 1. Initial Local Hydration (loads instantly so user has zero lag)
  useEffect(() => {
    if (!userId) return;
    
    const store = loadStore(userId);
    let localUpdatedAt = 0;
    if (store && typeof store === 'object') {
      setProjects(Array.isArray(store.projects) ? store.projects : []);
      setConversations(Array.isArray(store.conversations) ? store.conversations : []);
      setActiveProjectId(store.activeProjectId || null);
      setConversation(
        store.conversation && Array.isArray(store.conversation.turns)
          ? store.conversation
          : makeEmptyConversation()
      );
      // Read the saved timestamp if present, otherwise default to 0
      localUpdatedAt = store.updatedAt || 0;
    } else {
      // Clear data to protect the new account's workspace privacy
      setProjects([]);
      setConversations([]);
      setActiveProjectId(null);
      setConversation(makeEmptyConversation());
    }
    
    // Register the current userId and its timestamp as loaded
    loadedUserIdRef.current = userId;
    lastSyncTimeRef.current = localUpdatedAt;

    // 2. Fetch the latest state from Cloudflare D1 (silently in background)
    if (userId !== 'anonymous') {
      fetch(`${API_URL}/api/workspace/sync`, {
        headers: { 'x-user-id': userId }
      })
        .then(res => res.json())
        .then(res => {
          if (res.success && res.data) {
            const cloudUpdatedAt = res.updatedAt || 0;
            // CONFLICT RESOLUTION: Hydrate only if cloud state is strictly newer than our local state!
            if (cloudUpdatedAt > lastSyncTimeRef.current) {
              const cloudStore = res.data;
              setProjects(Array.isArray(cloudStore.projects) ? cloudStore.projects : []);
              setConversations(Array.isArray(cloudStore.conversations) ? cloudStore.conversations : []);
              setActiveProjectId(cloudStore.activeProjectId || null);
              setConversation(
                cloudStore.conversation && Array.isArray(cloudStore.conversation.turns)
                  ? cloudStore.conversation
                  : makeEmptyConversation()
              );
              lastSyncTimeRef.current = cloudUpdatedAt;
              
              // Keep our localStorage cache aligned
              saveStore(
                {
                  projects: cloudStore.projects,
                  conversations: cloudStore.conversations,
                  conversation: cloudStore.conversation,
                  activeProjectId: cloudStore.activeProjectId,
                  updatedAt: cloudUpdatedAt
                },
                userId
              );
              console.log(`[Workspace Cloud Sync] Successfully hydrated newer state from Cloudflare D1 (${new Date(cloudUpdatedAt).toLocaleTimeString()})`);
            } else if (lastSyncTimeRef.current > cloudUpdatedAt) {
              // Local is newer! Automatically push local to Cloudflare D1 to synchronize it
              const currentStore = loadStore(userId);
              if (currentStore) {
                fetch(`${API_URL}/api/workspace/sync`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
                  body: JSON.stringify({ data: currentStore, updatedAt: lastSyncTimeRef.current })
                }).catch(err => console.error('[Workspace Cloud Sync] Failed pushing newer local state:', err.message));
              }
            }
          }
        })
        .catch(err => console.warn('[Workspace Cloud Sync] Background sync fetch failed:', err?.message));
    }

    // Mark loading complete with state-batching so persistence hook knows state variables are ready
    setLoadedUserId(userId);
  }, [userId]);

  // 3. Persist on changes (Optimistic Save locally, Debounced background save to D1)
  useEffect(() => {
    // CRITICAL: Avoid saving unless the state loaded represents the active user!
    // This state-batched guard prevents the critical React rendering race condition 
    // where changing userId triggers Step 3 with User A's projects before User B's state settles.
    if (!loadedUserId || loadedUserId !== userId) return;
    if (!loadedUserIdRef.current || loadedUserIdRef.current !== userId) return;
    
    const newTimestamp = Date.now();
    lastSyncTimeRef.current = newTimestamp;

    const payload = { projects, conversations, conversation, activeProjectId, updatedAt: newTimestamp };
    
    // Write instantly to local storage for absolute zero-lag performance
    saveStore(payload, userId);

    if (userId === 'anonymous') return;

    // Debounce the D1 write by 2 seconds to avoid excessive network requests on every keypress!
    const timer = setTimeout(() => {
      fetch(`${API_URL}/api/workspace/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId
        },
        body: JSON.stringify({
          data: { projects, conversations, conversation, activeProjectId },
          updatedAt: newTimestamp
        })
      })
        .then(res => res.json())
        .then(res => {
          if (res.success) {
            console.log('[Workspace Cloud Sync] Cloudflare D1 edge state synchronized successfully');
          }
        })
        .catch(err => console.warn('[Workspace Cloud Sync] Cloud sync update failed:', err?.message));
    }, 2000);

    return () => clearTimeout(timer);
  }, [projects, conversations, conversation, activeProjectId, userId]);

  const clearAttachments = useCallback(() => setAttachments([]), []);

  // Mutators ------------------------------------------------------
  const updateMode = useCallback((mode, patch) => {
    setModes(prev => ({ ...prev, [mode]: { ...prev[mode], ...patch } }));
  }, []);

  const switchMode = useCallback((newMode, opts = {}) => {
    if (!MODE_IDS.includes(newMode)) return;
    setActiveMode(newMode);
    if (opts.carryPrompt && typeof opts.prompt === 'string') {
      setDraftPrompt(opts.prompt);
    }
  }, []);

  const parseSlashCommand = useCallback((text) => {
    if (typeof text !== 'string') return null;
    const match = text.match(/^\s*\/(chat|image|video|music|slides)(?:\s+([\s\S]*))?$/i);
    if (!match) return null;
    return { mode: match[1].toLowerCase(), rest: (match[2] || '').trim() };
  }, []);

  // ─── Thread actions ──────────────────────────────────────────────
  const appendTurn = useCallback((turn) => {
    setConversation(prev => ({
      ...prev,
      turns: [...prev.turns, turn],
      updatedAt: Date.now(),
    }));
  }, []);

  const patchTurn = useCallback((turnId, patch) => {
    setConversation(prev => ({
      ...prev,
      turns: prev.turns.map(t => t.id === turnId ? { ...t, ...patch } : t),
      updatedAt: Date.now(),
    }));
  }, []);

  /**
   * sendTurn - commits the current draft as a user turn of the active mode,
   * then dispatches a simulated assistant turn (Phase-3b hooks real APIs here).
   *
   * The active mode decides the type; draft controls travel in meta.
   */
  const sendTurn = useCallback(() => {
    const prompt = draftPrompt.trim();
    if (!prompt) return;

    const lowerPrompt = prompt.toLowerCase();
    const isNewThreadTrigger = [
      'new thread',
      'new conversation',
      'start a new thread',
      'start new thread',
      'start a new conversation',
      'start new conversation',
      'clear thread',
      'clear chat',
      'clear conversation',
      '/new',
      '/newthread',
      '/clear'
    ].includes(lowerPrompt);

    if (isNewThreadTrigger) {
      const now = Date.now();
      
      // 1) User turn
      const userTurn = {
        id: uid(),
        role: 'user',
        type: 'chat',
        status: 'done',
        createdAt: now,
        text: prompt,
        meta: {},
      };

      // 2) Assistant turn
      const assistantTurn = {
        id: uid(),
        role: 'assistant',
        type: 'chat',
        status: 'done',
        createdAt: now + 1,
        text: 'Got it — starting fresh. What do you want to work on next?',
        meta: { 
          isResetTurn: true,
          replyTo: userTurn.id, 
          suggestions: [
            "Can you write a script for a product intro?",
            "How do I generate an image using Imagen 4?",
            "Can you explain smart contracts in blockchain?"
          ] 
        },
      };

      // Stop ongoing generation and clear inputs
      stopGeneration();
      setDraftPrompt('');
      setAttachments([]);

      setConversation(prev => {
        return {
          ...prev,
          turns: [...prev.turns, userTurn, assistantTurn],
          updatedAt: now
        };
      });
      return;
    }

    const mode = activeMode;
    const ctrl = modes[mode] || {};
    const currentAttachments = attachments && attachments.length > 0 ? [...attachments] : [];
    
    // Merge attachments into options if applicable
    const generationOptions = { ...ctrl };
    
    // 1. Manual attachments (Highest priority)
    if (currentAttachments.length > 0) {
      if (mode === 'image') {
        generationOptions.image_url = currentAttachments[0].url;
        generationOptions.image_input = currentAttachments.map(a => a.url);
      } else if (mode === 'video') {
        generationOptions.image_url = currentAttachments[0].url;
        generationOptions.image_urls = currentAttachments.map(a => a.url);
      }
    } 
    // 2. Smart Context: Use intent-resolved reference if present
    else if (intent && intent.referenceTurnId) {
      const refTurn = conversation.turns.find(t => t.id === intent.referenceTurnId);
      if (refTurn && refTurn.imageUrl && mode === 'video') {
        generationOptions.image_url = refTurn.imageUrl;
        generationOptions.image_urls = [refTurn.imageUrl];
        generationOptions.context_image_ref = refTurn.id;
      }
    }
    // 3. Auto-fallback: For video, grab the absolute most recent image if none provided
    else if (mode === 'video' && conversation && conversation.turns) {
      const lastImageTurn = [...conversation.turns].reverse().find(t => t.imageUrl && t.status === 'done');
      if (lastImageTurn) {
        generationOptions.image_url = lastImageTurn.imageUrl;
        generationOptions.image_urls = [lastImageTurn.imageUrl];
        generationOptions.context_image_ref = lastImageTurn.id;
      }
    }

    const now = Date.now();
    const userId = userProfile?.id || '';

    // Final resolved reference for the UI memory ribbon
    const resolvedRefId = (intent && intent.referenceTurnId) || generationOptions.context_image_ref;

    // 1) User turn (attach memory-ribbon link if intent resolved a reference)
    const userTurn = {
      id: uid(),
      role: 'user',
      type: mode,
      status: 'done',
      createdAt: now,
      text: prompt,
      meta: {
        ...ctrl,
        attachments: currentAttachments.length > 0 ? currentAttachments : undefined,
        ...(resolvedRefId ? { referenceTurnId: resolvedRefId } : {}),
        ...(intent && intent.source ? { intentSource: intent.source } : {}),
      },
    };

    // 2) Assistant pending turn
    const assistantTurn = {
      id: uid(),
      role: 'assistant',
      type: mode,
      status: 'pending',
      createdAt: now + 1,
      meta: { ...ctrl, replyTo: userTurn.id },
    };

    // Snapshot prior turns for context before we mutate state
    const priorTurns = conversation.turns;
    
    let targetProjectId = activeProjectId || conversation.projectId;
    if (!targetProjectId) {
      targetProjectId = uid();
      const projTitle = truncateTitle(prompt) || 'Untitled project';
      const proj = {
        id: targetProjectId, title: projTitle,
        color: '#7C3AED', starred: false,
        conversationIds: [conversation.id],
        createdAt: now, updatedAt: now,
      };
      setProjects(prev => [proj, ...prev]);
      setActiveProjectId(targetProjectId);
    }

    setConversation(prev => {
      const next = {
        ...prev,
        projectId: targetProjectId,
        title: prev.turns.length === 0 ? truncateTitle(prompt) : prev.title,
        turns: [...prev.turns, userTurn, assistantTurn],
        updatedAt: now,
      };
      return next;
    });

    // Clear attachments ONLY after the turn is safely in the state
    setAttachments([]);

    // Bump the owning project's updatedAt so the sidebar shows the latest activity.
    if (targetProjectId) {
      setProjects(prev => prev.map(p =>
        p.id === targetProjectId ? { ...p, updatedAt: now } : p
      ));
    }
    setDraftPrompt('');

    const markDone = (patch) => {
      setConversation(prev => ({
        ...prev,
        turns: prev.turns.map(t => t.id === assistantTurn.id
          ? { ...t, ...patch, status: 'done' }
          : t),
        updatedAt: Date.now(),
      }));

      // Async DB Persistence for User Dashboard Gallery syncing
      if (userId && userId !== 'anonymous') {
        const promptText = prompt || 'Generated media';
        const modelName = ctrl?.model || 'AI Model';

        if (patch.imageUrl) {
          contentService.saveImage(userId, {
            prompt: promptText,
            model: modelName,
            url: patch.imageUrl,
            width: patch.meta?.width || 1024,
            height: patch.meta?.height || 1024,
            metadata: patch.meta,
          }).then(res => console.log('[WorkspaceDB] Saved image:', res))
            .catch(err => console.warn('[WorkspaceDB] Error saving image:', err));
        } else if (patch.videoUrl) {
          contentService.saveVideo(userId, {
            prompt: promptText,
            model: modelName,
            url: patch.videoUrl,
            duration: patch.meta?.duration || 4,
            metadata: patch.meta,
          }).then(res => console.log('[WorkspaceDB] Saved video:', res))
            .catch(err => console.warn('[WorkspaceDB] Error saving video:', err));
        } else if (patch.audioUrl) {
          contentService.saveMusic(userId, {
            prompt: promptText,
            model: modelName,
            url: patch.audioUrl,
            duration: patch.meta?.duration || 30,
            isInstrumental: patch.meta?.isInstrumental || false,
            metadata: patch.meta,
          }).then(res => console.log('[WorkspaceDB] Saved music:', res))
            .catch(err => console.warn('[WorkspaceDB] Error saving music:', err));
        }
      }
    };

    const markError = (message) => {
      setConversation(prev => ({
        ...prev,
        turns: prev.turns.map(t => t.id === assistantTurn.id
          ? { ...t, status: 'error', error: message, text: `⚠ ${message}` }
          : t),
        updatedAt: Date.now(),
      }));
    };

    // 3) Dispatch real generation ---------------------------------
    if (mode === 'chat' || mode === 'slides') {
      let resolvedPrompt = prompt;
      if (mode === 'slides') {
        resolvedPrompt = `${prompt}\n\n[System Request: You are an expert Presentation Designer. Respond in standard markdown.
And YOU MUST append a structured JSON presentation block at the very end of your response, strictly formatted as:
[PRESENTATION_DATA]
{
  "title": "Presentation Title",
  "slides": [
    {
      "slideNumber": 1,
      "title": "Slide Title",
      "subtitle": "Subtitle or topic focus",
      "content": [
        "First key value proposition bullet",
        "Second key insights or supporting detail bullet",
        "Third actionable takeaway bullet"
      ],
      "visualDescription": "High-fidelity visual layout description",
      "imagePrompt": "A clean, modern, ultra-high-quality professional isolated vector illustration of... on clean modern light-tint background, perfect for slide graphic"
    }
  ]
}
[/PRESENTATION_DATA]
Make sure every slide contains high-value structured information, professional landscape visual ideas, and clean wording. The imagePrompts will be used to generate beautiful visual blocks. Do not omit any slide numbers.]`;
      }
      const messages = buildMessagesFromTurns(priorTurns, resolvedPrompt, attachments);
      let fullText = '';
      let suggestions = null;
      let modelUsed = ctrl.model;
      let webSearchUsed = false;
      let citationsList = []; // local to track citations

      // Create new AbortController
      const controller = new AbortController();
      activeAbortController.current = controller;

      // Dispatch streaming chat request
      sendChatStream({
          modelId: ctrl.model,
          messages,
          userId,
          webSearch: ctrl.webSearch === false ? false : true,
          localTime: new Date().toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
          signal: controller.signal,
          onUpdate: (ev) => {
            try {
              console.log('[WorkspaceContext] onUpdate:', ev);
              if (ev.type === 'content') {
                fullText += ev.content;
                patchTurn(assistantTurn.id, { text: fullText });
              } else if (ev.type === 'status') {
                setConversation(prev => ({
                  ...prev,
                  turns: prev.turns.map(t => t.id === assistantTurn.id
                    ? { ...t, statusEvents: [...(t.statusEvents || []), ev] }
                    : t)
                }));
              } else if (ev.type === 'metadata') {
                if (ev.modelUsed) modelUsed = ev.modelUsed;
                if (ev.webSearchUsed) webSearchUsed = ev.webSearchUsed;
                if (ev.citations) {
                  citationsList = ev.citations;
                  patchTurn(assistantTurn.id, { meta: { ...assistantTurn.meta, citations: ev.citations } });
                }
              } else if (ev.type === 'done') {
                console.log('[WorkspaceContext] Stream Done. Final text length:', fullText.length);
                let finalText = fullText;
                
                // Parse for SUGGESTIONS array
                const suggestionsMatch = finalText.match(/\[SUGGESTIONS:\s*(\[.*?\])\s*\]/);
                if (suggestionsMatch) {
                  try {
                    suggestions = JSON.parse(suggestionsMatch[1]);
                    finalText = finalText.replace(suggestionsMatch[0], '').trim();
                  } catch (e) { console.warn('Failed to parse suggestions:', e); }
                }

                // Fallback suggestions in case response truncated or had none
                if (!suggestions || !Array.isArray(suggestions) || suggestions.length === 0) {
                  if (mode === 'chat') {
                    suggestions = [
                      "Can you go deeper into the first module?",
                      "What are some real-world industry use cases?",
                      "Could you give me a hands-on exercise to practice?"
                    ];
                  } else if (mode === 'image') {
                    suggestions = [
                      "Make it more cinematic and dramatic",
                      "Try a different art style for this",
                      "Describe the visual details in depth"
                    ];
                  } else {
                    suggestions = [
                      "Could you refine this generation?",
                      "Show me alternative options",
                      "Explain how I can optimize the settings"
                    ];
                  }
                }

                // Parse for EXECUTE_GENERATION trigger
                const execMatch = finalText.match(/\[EXECUTE_GENERATION:\s*(\{.*?\})\s*\]/i);
                let execJson = null;
                if (execMatch) {
                  try {
                    execJson = JSON.parse(execMatch[1]);
                    finalText = finalText.replace(execMatch[0], '').trim();
                  } catch (e) { console.warn('Failed to parse EXECUTE_GENERATION JSON:', e); }
                }

                if (execJson && execJson.mode) {
                  const targetMode = execJson.mode.toLowerCase();
                  if (!finalText) finalText = `[Executing ${targetMode} generation...]`;

                  markDone({
                    text: finalText,
                    meta: { ...ctrl, replyTo: userTurn.id, modelUsed, webSearchUsed, suggestions, citations: citationsList },
                  });

                  // Trigger the actual generation
                  const extractedParams = {};
                  if (execJson.aspect_ratio) {
                    const arMatch = execJson.aspect_ratio.match(/(\d+:\d+)/);
                    if (arMatch) extractedParams.aspect = arMatch[1];
                  }
                  if (execJson.style) extractedParams.style = execJson.style;
                  if (execJson.duration) extractedParams.duration = parseInt(execJson.duration);
                  
                  if (Object.keys(extractedParams).length > 0) updateMode(targetMode, extractedParams);
                  setActiveMode(targetMode);

                  generateUnified({
                    mode: targetMode,
                    modelId: extractedParams.model || modes[targetMode]?.model,
                    messages: buildMessagesFromTurns(priorTurns, prompt, attachments),
                    options: { ...modes[targetMode], ...extractedParams },
                    userId
                  }).then(({ taskId, synthesizedPrompt }) => {
                    const genTurnId = uid();
                    appendTurn({
                      id: genTurnId,
                      role: 'assistant',
                      type: targetMode,
                      status: 'pending',
                      createdAt: Date.now(),
                      meta: { ...extractedParams, replyTo: userTurn.id }
                    });

                    keiAIService.pollTaskStatus(taskId).then(result => {
                      if (targetMode === 'image') patchTurn(genTurnId, { status: 'done', imageUrl: result.imageUrl, meta: { ...extractedParams, synthesizedPrompt } });
                      else if (targetMode === 'video') patchTurn(genTurnId, { status: 'done', videoUrl: result.imageUrl, meta: { ...extractedParams, synthesizedPrompt } });
                      else if (targetMode === 'music') patchTurn(genTurnId, { status: 'done', audioUrl: result.imageUrl, meta: { ...extractedParams, synthesizedPrompt, sunoTracks: result.allSunoTracks } });
                    }).catch(e => {
                      console.error('Task status polling failed:', e);
                      patchTurn(genTurnId, { status: 'error', error: e.message });
                    });
                  }).catch(err => {
                    console.error('Failed executing inner generation:', err);
                    appendTurn({
                      id: uid(),
                      role: 'assistant',
                      type: targetMode,
                      status: 'error',
                      createdAt: Date.now(),
                      text: `⚠ Generation failed to start: ${err.message}`,
                      meta: { replyTo: userTurn.id }
                    });
                  });
                } else {
                  markDone({
                    text: finalText,
                    meta: { ...ctrl, replyTo: userTurn.id, modelUsed, webSearchUsed, suggestions, citations: citationsList },
                  });
                }
              }
            } catch (err) {
              console.error('[WorkspaceContext] Error in onUpdate callback handler:', err);
              markError(err?.message || 'Error processing AI response event');
            }
          }
        }).catch(err => {
          if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
            console.log('[Workspace] Stream aborted by user');
            markDone({
              text: fullText + '\n\n*Generation stopped by user.*',
              meta: { ...ctrl, replyTo: userTurn.id, modelUsed, webSearchUsed, suggestions, citations: citationsList }
            });
          } else {
            console.error('[Workspace] chat failed:', err);
            markError(err?.message || 'Chat request failed');
          }
        }).finally(() => {
          if (activeAbortController.current === controller) {
            activeAbortController.current = null;
          }
        });
    } else {
      // Image / Video / Music - True generation via Memory Synthesizer
      const messages = buildMessagesFromTurns(priorTurns, prompt, attachments);
      
      const generationOptions = { ...ctrl };
      
      // Smart Context: If this is a video/image generation referencing a prior turn
      if (intent.referenceTurnId) {
        const refTurn = conversation.turns.find(t => t.id === intent.referenceTurnId);
        if (refTurn) {
          if (mode === 'video' && refTurn.imageUrl) {
            generationOptions.imageInput = refTurn.imageUrl;
            console.log('[Workspace] Smart Context: Using image from turn', refTurn.id, 'as video input');
          } else if (mode === 'video' && refTurn.videoUrl) {
            generationOptions.videoInput = refTurn.videoUrl;
            console.log('[Workspace] Smart Context: Using video from turn', refTurn.id, 'as video input');
          }
        }
      }

      const controller = new AbortController();
      activeAbortController.current = controller;

      const doGeneration = async () => {
        try {
          const { taskId, synthesizedPrompt } = await generateUnified({
            mode,
            modelId: ctrl.model,
            messages,
            options: generationOptions,
            userId
          });

          if (controller.signal.aborted) {
            throw new DOMException('Aborted', 'AbortError');
          }

          // Poll for the result, passing our controller's abort signal
          const result = await keiAIService.pollTaskStatus(taskId, ctrl.model, 180, 2000, controller.signal);
          
          if (mode === 'image') markDone({ imageUrl: result.imageUrl, meta: { ...ctrl, replyTo: userTurn.id, synthesizedPrompt } });
          else if (mode === 'video') markDone({ videoUrl: result.imageUrl, meta: { ...ctrl, replyTo: userTurn.id, synthesizedPrompt } });
          else if (mode === 'music') {
            const extraData = result.allSunoTracks?.length ? { sunoTracks: result.allSunoTracks } : {};
            markDone({ audioUrl: result.imageUrl, meta: { ...ctrl, replyTo: userTurn.id, synthesizedPrompt, ...extraData } });
          }
          else markDone({ text: 'Generation complete.', meta: { ...ctrl, replyTo: userTurn.id } });
          
        } catch (err) {
          if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
            console.log('[Workspace] Unified generation aborted by user');
            markDone({
              text: 'Generation stopped by user.',
              meta: { ...ctrl, replyTo: userTurn.id }
            });
          } else {
            console.error('[Workspace] unified generation failed:', err);
            markError(err?.message || 'Generation failed');
          }
        } finally {
          pendingTimers.current.delete(assistantTurn.id);
          if (activeAbortController.current === controller) {
            activeAbortController.current = null;
          }
        }
      };

      doGeneration();
    }
  }, [draftPrompt, activeMode, modes, conversation, userProfile, intent, attachments]);

  const newConversation = useCallback((opts = {}) => {
    stopGeneration();
    // Cancel pending simulations
    pendingTimers.current.forEach(t => clearTimeout(t));
    pendingTimers.current.clear();

    const newConv = makeEmptyConversation();
    if (opts.projectId) newConv.projectId = opts.projectId;

    setConversation(prev => {
      // archive the current one if it has any turns
      if (prev.turns.length > 0) {
        setConversations(c => [prev, ...c].slice(0, 50));
      }
      return newConv;
    });
    setDraftPrompt('');
    setIntent({ mode: 'chat', confidence: 0, source: 'default' });
    setIntentLocked(false);
    setActivePreview(null);
    setAttachments([]);
    if (activeMode !== 'slides') {
      setActiveMode('chat');
    }

    // If this conversation belongs to a project, register it.
    if (opts.projectId) {
      setProjects(prev => prev.map(p =>
        p.id === opts.projectId
          ? { ...p, conversationIds: [...(p.conversationIds || []), newConv.id], updatedAt: Date.now() }
          : p
      ));
    }
    return newConv.id;
  }, []);

  const loadConversation = useCallback((id) => {
    // 1) check archived list
    const found = conversations.find(c => c.id === id);
    if (found) {
      setConversation(prev => {
        if (prev.turns.length > 0) {
          setConversations(c => [prev, ...c.filter(x => x.id !== found.id)].slice(0, 50));
        } else {
          setConversations(c => c.filter(x => x.id !== found.id));
        }
        return found;
      });
      return;
    }
    // 2) if it's the current one, no-op
    if (conversation.id === id) return;
  }, [conversations, conversation.id]);

  const deleteConversation = useCallback((id) => {
    setConversations(prev => prev.filter(c => c.id !== id));
    setConversation(prev => {
      if (prev.id === id) {
        const c = makeEmptyConversation();
        c.projectId = activeProjectId;
        return c;
      }
      return prev;
    });
  }, [activeProjectId]);

  // ─── Project mutators ─────────────────────────────────────────
  const createProject = useCallback((title = 'Untitled project') => {
    const id = uid();
    const proj = {
      id, title,
      color: '#7C3AED',
      starred: false,
      conversationIds: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    setProjects(prev => [proj, ...prev]);
    setActiveProjectId(id);
    // Also start a fresh conversation owned by this project so the user can
    // immediately start typing.
    setConversation(prev => {
      if (prev.turns.length > 0) {
        setConversations(c => [prev, ...c].slice(0, 50));
      }
      const c = makeEmptyConversation();
      c.projectId = id;
      proj.conversationIds.push(c.id);
      return c;
    });
    setDraftPrompt('');
    setActiveMode('chat');
    return id;
  }, []);

  const renameProject = useCallback((id, title) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, title, updatedAt: Date.now() } : p));
  }, []);

  const togglePinProject = useCallback((id) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, starred: !p.starred, updatedAt: Date.now() } : p));
  }, []);

  const deleteProject = useCallback((id) => {
    setProjects(prev => prev.filter(p => p.id !== id));
    setConversations(prev => prev.filter(c => c.projectId !== id));
    setActiveProjectId(prev => prev === id ? null : prev);
    setConversation(prev => prev.projectId === id ? makeEmptyConversation() : prev);
  }, []);

  const openProject = useCallback((id) => {
    setActiveProjectId(id);
    // Find the most recent conversation in this project (current first, then archived)
    if (conversation.projectId === id) return;
    const archived = conversations.find(c => c.projectId === id);
    if (archived) {
      setConversation(prev => {
        if (prev.turns.length > 0) {
          setConversations(c => [prev, ...c.filter(x => x.id !== archived.id)].slice(0, 50));
        }
        return archived;
      });
    } else {
      // No conversation yet - start a fresh one for this project
      setConversation(prev => {
        if (prev.turns.length > 0) {
          setConversations(c => [prev, ...c].slice(0, 50));
        }
        const c = makeEmptyConversation();
        c.projectId = id;
        setProjects(p => p.map(x => x.id === id ? { ...x, conversationIds: [...(x.conversationIds || []), c.id] } : x));
        return c;
      });
    }
    setDraftPrompt('');
    setActiveMode('chat');
  }, [conversation.projectId, conversations]);

  // ─── Return to the Bento home shell ──────────────────────────
  // Archives the current conversation (if it has turns), clears the active
  // project + draft so the ThreadView switches back to <BentoShell />.
  const goHome = useCallback((keepMode = false) => {
    setConversation(prev => {
      if (prev.turns.length > 0) {
        setConversations(c => [prev, ...c.filter(x => x.id !== prev.id)].slice(0, 50));
      }
      return makeEmptyConversation();
    });
    setActiveProjectId(null);
    setDraftPrompt('');
    if (!keepMode) {
      setActiveMode('chat');
    }
  }, []);

  // ─── Intent override (called by composer chip) ────────────────
  const overrideIntent = useCallback((mode) => {
    if (!MODE_IDS.includes(mode)) return;
    setIntent(prev => ({ ...prev, mode, source: 'manual', confidence: 1 }));
    setIntentLocked(true);
    setActiveMode(mode);
  }, []);

  // Helpers the prompt bar can still call in its current shape ----
  const currentMode = modes[activeMode];

  // ─── Auto-intent: re-classify whenever the draft changes ─────
  // Wrap setDraftPrompt so consumers don't have to know about intent.
  const setDraftPromptWithIntent = useCallback((next) => {
    setDraftPrompt(next);
    if (intentLocked) return;
    const det = detectIntent(next, conversation.turns);
    setIntent(det);
    // Mirror the detected mode into activeMode when it's reasonably confident,
    // so existing modes[mode] / sendTurn paths keep working without changes.
    // If it's a confident slash command, or a keyword, pronoun, etc., switch the mode.
    // We only exclude default fallback to prevent empty prompts from resetting an intentional mode.
    if (det.confidence >= 0.75 && det.mode !== activeMode && det.source !== 'default') {
      setActiveMode(det.mode);
    } else if (det.source === 'slash' && det.mode !== activeMode) {
      setActiveMode(det.mode);
    }
  }, [intentLocked, conversation.turns, activeMode]);

  const stopGeneration = useCallback(() => {
    if (activeAbortController.current) {
      activeAbortController.current.abort();
      activeAbortController.current = null;
    }
  }, []);

  // Effective sendTurn: clear intent lock after sending
  const sendTurnWithIntentReset = useCallback(() => {
    sendTurn();
    setIntentLocked(false);
    setIntent({ mode: 'chat', confidence: 0, source: 'default' });
  }, [sendTurn]);

  const value = useMemo(() => ({
    // Draft
    activeMode,
    draftPrompt,
    setDraftPrompt: setDraftPromptWithIntent,
    modes,
    currentMode,
    switchMode,
    updateMode,
    parseSlashCommand,

    // Auto-intent
    intent,
    intentLocked,
    overrideIntent,

    // Catalog (hydrated from /api/models)
    availableModels,
    defaultModels,

    // Thread
    conversation,
    conversations,
    appendTurn,
    patchTurn,
    sendTurn: sendTurnWithIntentReset,
    newConversation,
    loadConversation,
    deleteConversation,
    stopGeneration,
    generateSlideImage: async (promptText) => {
      const messages = [{ role: 'user', content: promptText }];
      const { taskId } = await generateUnified({
        mode: 'image',
        modelId: 'gpt-image-2',
        messages,
        options: { aspect: '16:9', style: 'Modern Minimalist Vector' },
        userId
      });
      const result = await keiAIService.pollTaskStatus(taskId, 'gpt-image-2', 180, 2000);
      if (!result || !result.imageUrl) {
        throw new Error('No image URL returned from Kie.ai generator');
      }
      return { imageUrl: result.imageUrl };
    },

    // Projects (Atelier)
    projects,
    activeProjectId,
    createProject,
    renameProject,
    togglePinProject,
    deleteProject,
    openProject,
    goHome,
    expandedMedia,
    setExpandedMedia,
    activePreview,
    setActivePreview,
    attachments,
    setAttachments,
    clearAttachments,
    generateUnified,
  }), [activeMode, draftPrompt, modes, currentMode, availableModels, defaultModels,
       conversation, conversations,
       intent, intentLocked, overrideIntent,
       projects, activeProjectId, expandedMedia, activePreview,
       attachments, clearAttachments,
       switchMode, updateMode, parseSlashCommand,
       appendTurn, patchTurn, sendTurnWithIntentReset, newConversation, loadConversation, deleteConversation, stopGeneration,
       createProject, renameProject, togglePinProject, deleteProject, openProject, goHome,
       setDraftPromptWithIntent, generateUnified, userId]);

  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}



export function useWorkspace() {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}

// ─── Helpers ────────────────────────────────────────────────────
/** Prefer xAI Grok, then GPT-4o, then Claude 3.5 Sonnet. */
function pickDefaultChatModel(list) {
  if (!list || !list.length) return null;
  const byId = (needle) => list.find(m => (m.id || '').toLowerCase().includes(needle));
  return (byId('grok') || byId('gpt-4o') || byId('claude-3.5-sonnet')
       || byId('gemini-2.5-pro') || list[0]).id;
}

function truncateTitle(s, max = 56) {
  const t = s.trim().replace(/\s+/g, ' ');
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
}

// Helpers removed in favor of real endpoints
